"use client"

/**
 * Grading submissions secondary panel — one roster with search.
 * Each row shows a # avatar until fully scored, then student avatar and name.
 */

import * as React from "react"

import { HubScoreCell } from "@/components/grading/hub-score-cell"
import {
  SubmissionCountAvatar,
  SubmissionRowAvatar,
  hubSubmissionScoringStatus,
  submissionCompactNumber,
  submissionRosterStatusBadge,
} from "@/components/grading/submission-identity-markers"
import { SidebarCollapsedSearchButton } from "@/components/sidebar"
import { useSecondaryHubNavChrome } from "@/components/sidebar"
import { useSecondaryPanel } from "@/components/sidebar"
import { Button } from "@/components/ui/button"
import { Tip } from "@/components/ui/tip"
import { GRADING_SUBMISSIONS_SECONDARY_PANEL_ID } from "@/lib/submission-detail-nav"
import {
  submissionRosterLabel,
  submissionShowsStudentIdentity,
  type AssessmentGradingState,
  type GradingSubmission,
} from "@/lib/mock/exam-grading"
import { cn } from "@/lib/utils"

function submissionSearchHaystack(
  state: AssessmentGradingState,
  row: GradingSubmission,
  index: number,
): string {
  const num = submissionCompactNumber(row, index)
  const parts = [
    row.maskLabel,
    `Submission ${num}`,
    `#${num}`,
    String(num),
    row.studentId,
    row.studentEmail,
  ]
  if (submissionShowsStudentIdentity(state, row)) {
    parts.push(row.studentName)
  }
  return parts.join(" ").toLowerCase()
}

function filterSubmissions(
  rows: GradingSubmission[],
  state: AssessmentGradingState,
  query: string,
): GradingSubmission[] {
  const q = query.trim().toLowerCase()
  if (!q) return rows
  return rows.filter((row, index) =>
    submissionSearchHaystack(state, row, index).includes(q),
  )
}

function submissionRowClass(active: boolean, discarded: boolean) {
  return cn(
    "flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
    active
      ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground ring-1 ring-inset ring-sidebar-border/80"
      : "text-sidebar-foreground hover:bg-sidebar-accent/50",
    discarded && "opacity-80",
  )
}

function GradingRosterSearch({
  id,
  value,
  onChange,
}: {
  id: string
  value: string
  onChange: (value: string) => void
}) {
  const placeholder = "Search submissions"
  return (
    <div className="shrink-0 px-2 pb-2 pt-1">
      <label htmlFor={id} className="sr-only">
        {placeholder}
      </label>
      <div className="relative">
        <i
          className="fa-light fa-magnifying-glass pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground"
          aria-hidden="true"
        />
        <input
          id={id}
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className={cn(
            "h-7 w-full rounded-md border border-border bg-background ps-7 pe-2 text-base md:text-xs",
            "placeholder:text-placeholder-foreground",
            "focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring",
          )}
        />
      </div>
    </div>
  )
}

function SubmissionSecondaryNavRow({
  row,
  index,
  selected,
  onSelect,
}: {
  row: GradingSubmission
  index: number
  selected: boolean
  onSelect: () => void
}) {
  const { gradingSubmissionsBridge } = useSecondaryPanel()
  const state = gradingSubmissionsBridge?.state
  if (!state) return null

  const showStudent = submissionShowsStudentIdentity(state, row)
  const status = hubSubmissionScoringStatus(row)
  const submissionNumber = submissionCompactNumber(row, index)
  const primaryLabel = submissionRosterLabel(state, row)

  return (
    <li className="min-w-0">
      <button
        type="button"
        aria-current={selected ? "true" : undefined}
        className={submissionRowClass(selected, row.thrownOut)}
        onClick={onSelect}
      >
        {showStudent ? (
          <SubmissionRowAvatar row={row} />
        ) : (
          <SubmissionCountAvatar number={submissionNumber} status={status} />
        )}

        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-start justify-between gap-2">
            <span className="min-w-0 truncate font-medium leading-snug">
              {primaryLabel}
            </span>
            {submissionRosterStatusBadge(state, row)}
          </span>
          {showStudent ? (
            <span className="mt-0.5 block truncate text-xs text-muted-foreground">
              {row.studentEmail}
            </span>
          ) : null}
          <span className="mt-0.5 block">
            <HubScoreCell state={state} row={row} />
          </span>
        </span>
      </button>
    </li>
  )
}

export function SubmissionsSecondaryNav() {
  const { gradingSubmissionsBridge, openPanel } = useSecondaryPanel()
  const { showCompactRail, dismissNavFlyout } = useSecondaryHubNavChrome()
  const [searchQuery, setSearchQuery] = React.useState("")

  const submissions = gradingSubmissionsBridge?.submissions ?? []
  const currentSubmissionId = gradingSubmissionsBridge?.currentSubmissionId
  const onSelect = gradingSubmissionsBridge?.onSelect
  const state = gradingSubmissionsBridge?.state

  const expandForSearch = React.useCallback(() => {
    openPanel(GRADING_SUBMISSIONS_SECONDARY_PANEL_ID)
  }, [openPanel])

  if (submissions.length === 0 || !onSelect || !state) {
    return null
  }

  const filtered = filterSubmissions(submissions, state, searchQuery)

  if (showCompactRail) {
    return (
      <nav className="flex min-h-0 flex-1 flex-col" aria-label="Submissions">
        <div className="flex flex-col items-center gap-1 border-b border-sidebar-border/60 px-1 py-2">
          <Tip label="Show labels" side="right">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-9 shrink-0"
              aria-label="Show labels"
              onClick={expandForSearch}
            >
              <i
                className="fa-light fa-angles-right text-md"
                aria-hidden="true"
              />
            </Button>
          </Tip>
          <SidebarCollapsedSearchButton
            label="Search submissions"
            onActivate={expandForSearch}
          />
        </div>
        <ul className="flex flex-1 flex-col items-center gap-1 overflow-y-auto px-1 py-2">
          {filtered.map((row, index) => {
            const showStudent = submissionShowsStudentIdentity(state, row)
            const displayName = submissionRosterLabel(state, row)
            const status = hubSubmissionScoringStatus(row)
            const selected = row.id === currentSubmissionId
            const submissionNumber = submissionCompactNumber(row, index)

            const avatar = showStudent ? (
              <SubmissionRowAvatar row={row} />
            ) : (
              <SubmissionCountAvatar
                number={submissionNumber}
                status={status}
              />
            )

            return (
              <li key={row.id} className="flex w-full justify-center" role="none">
                <Tip label={displayName} side="right">
                  <button
                    type="button"
                    aria-current={selected ? "true" : undefined}
                    aria-label={displayName}
                    className={cn(
                      "flex size-9 shrink-0 items-center justify-center rounded-md transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                      selected
                        ? "bg-sidebar-accent text-sidebar-accent-foreground ring-1 ring-inset ring-sidebar-border/80"
                        : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                      row.thrownOut && "opacity-80",
                    )}
                    onClick={() => {
                      onSelect(row)
                      dismissNavFlyout()
                    }}
                  >
                    {avatar}
                  </button>
                </Tip>
              </li>
            )
          })}
        </ul>
      </nav>
    )
  }

  return (
    <div className="min-w-0 flex-1">
      <GradingRosterSearch
        id="grading-submissions-roster-search"
        value={searchQuery}
        onChange={setSearchQuery}
      />
      {filtered.length === 0 ? (
        <p className="px-3 py-4 text-center text-xs text-muted-foreground">
          No matches
        </p>
      ) : (
        <ul className="flex flex-col gap-0.5 px-1 pb-3">
          {filtered.map((row, index) => (
            <SubmissionSecondaryNavRow
              key={row.id}
              row={row}
              index={index}
              selected={row.id === currentSubmissionId}
              onSelect={() => {
                onSelect(row)
                dismissNavFlyout()
              }}
            />
          ))}
        </ul>
      )}
    </div>
  )
}
